// src/AccessManager.tsx
import React from "react";
import api from "./api/client";

type User = { id: string; email: string; name: string | null };
type Motor = { id: string; name: string };

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function genTempPassword(len = 16) {
  const chars =
    "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*()-_=+";
  return Array.from({ length: len }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
}

/** ---- Helpers pro sjednocení uživatelů z /users a /access ---- */
function normalizeUsers(arr: any[]): User[] {
  return (arr || [])
    .map((u: any) => ({
      id: u?.id ?? u?.userId ?? u?._id ?? u?.uid,
      email: u?.email,
      name: (u?.name ?? u?.fullName ?? null) as string | null,
    }))
    .filter((u) => u.id && u.email);
}

function unionUsers(a: User[], b: User[]) {
  const map = new Map<string, User>();
  for (const u of [...a, ...b]) {
    const key = (u.id as string) || u.email.toLowerCase();
    if (!map.has(key)) map.set(key, u);
  }
  return Array.from(map.values()).sort((x, y) =>
    (x.name || x.email).localeCompare(y.name || y.email, "cs"),
  );
}

/** ---- Bezpečné vytažení grants/matrix z odpovědi /access ---- */
function extractGrants(accessData: any): any[] {
  if (Array.isArray(accessData)) return accessData;
  if (accessData && typeof accessData === "object") {
    return (
      accessData.grants ||
      accessData.matrix ||
      accessData.permissions ||
      accessData.items ||
      []
    );
  }
  return [];
}

export default function AccessManager({
  open,
  onClose,
  motors,
}: {
  open: boolean;
  onClose: () => void;
  motors: Motor[];
}) {
  const [users, setUsers] = React.useState<User[]>([]);
  const [map, setMap] = React.useState<
    Record<string, { userId: string; motorId: string; canWrite: boolean }>
  >({});
  const key = (u: string, m: string) => `${u}|${m}`;

  const [busy, setBusy] = React.useState(false);
  const [creating, setCreating] = React.useState({
    email: "",
    name: "",
    role: "VIEWER" as "ADMIN" | "PLANNER" | "VIEWER",
    tempPassword: "",
  });

  /** ---- Načtení uživatelů + přístupů (union /users a /access) ---- */
  const loadUsersAndAccess = React.useCallback(async () => {
    const [uRes, aRes] = await Promise.all([
      api.get("/users", { params: { limit: 500 } }),
      api.get("/access"),
    ]);

    const usersA = normalizeUsers(uRes.data);
    const acc = aRes.data;
    const grants = extractGrants(acc);
    const usersB = normalizeUsers(Array.isArray(acc) ? [] : (acc?.users ?? []));
    setUsers(unionUsers(usersA, usersB));

    const m: Record<string, { userId: string; motorId: string; canWrite: boolean }> = {};
    for (const r of grants) {
      const userId =
        r?.userId ?? r?.user_id ?? r?.uid ?? r?.user?.id ?? r?.user?.userId;
      const motorId =
        r?.motorId ?? r?.motor_id ?? r?.mid ?? r?.motor?.id ?? r?.resourceId;
      const canWrite = !!(r?.canWrite ?? r?.write ?? r?.can_write ?? r?.canEdit);
      if (userId && motorId) m[key(userId, motorId)] = { userId, motorId, canWrite };
    }
    setMap(m);
  }, []);

  React.useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        await loadUsersAndAccess();
      } catch (e) {
        console.error("Failed to load access", e);
        alert("Nepodařilo se načíst přístupy/uživatele.");
      }
    })();
  }, [open, loadUsersAndAccess]);

  async function toggleRead(userId: string, motorId: string) {
    const k = key(userId, motorId);
    const exists = !!map[k];
    try {
      if (exists) {
        await api.delete("/access", { params: { userId, motorId } });
        setMap((prev) => {
          const c = { ...prev };
          delete c[k];
          return c;
        });
      } else {
        await api.put("/access", { userId, motorId, canWrite: false });
        setMap((prev) => ({ ...prev, [k]: { userId, motorId, canWrite: false } }));
      }
    } catch (e) {
      console.error("toggleRead failed", e);
    }
  }

  async function toggleWrite(userId: string, motorId: string) {
    const k = key(userId, motorId);
    const row = map[k];
    try {
      if (!row) {
        await api.put("/access", { userId, motorId, canWrite: true });
        setMap((prev) => ({ ...prev, [k]: { userId, motorId, canWrite: true } }));
      } else {
        await api.put("/access", { userId, motorId, canWrite: !row.canWrite });
        setMap((prev) => ({ ...prev, [k]: { ...row, canWrite: !row.canWrite } }));
      }
    } catch (e) {
      console.error("toggleWrite failed", e);
    }
  }

  async function createUser() {
    const email = creating.email.trim();
    if (!EMAIL_RX.test(email)) {
      alert("Zadej platný e-mail (např. uzivatel@example.com).");
      return;
    }
    setBusy(true);
    try {
      const basePayload: any = { email, role: creating.role };
      if (creating.name.trim()) basePayload.name = creating.name.trim();

      if (!creating.tempPassword) {
        // zkus bez hesla; když backend vyžaduje, vygenerujeme
        try {
          await api.post("/users", basePayload);
        } catch (err: any) {
          const needsPwd =
            err?.response?.status === 400 ||
            err?.response?.status === 422 ||
            String(err?.response?.data?.message || "").toLowerCase().includes("password");
          if (!needsPwd) throw err;
          const tmp = genTempPassword();
          await api.post("/users", { ...basePayload, password: tmp, requireReset: true });
        }
      } else {
        // admin zadal dočasné heslo → vynutíme reset vždy
        await api.post("/users", {
          ...basePayload,
          password: creating.tempPassword,
          requireReset: true,
        });
      }

      await loadUsersAndAccess();
      setCreating({ email: "", name: "", role: "VIEWER", tempPassword: "" });
      alert("Uživatel byl vytvořen.");
    } catch (err: any) {
      const msg =
        err?.response?.status === 409
          ? "Uživatel s tímto e-mailem už existuje."
          : err?.response?.data?.message ||
            "Nepodařilo se založit uživatele.";
      alert(msg);
      console.error(err);
    } finally {
      setBusy(false);
    }
  }

  // Zůstává jen ruční nastavení hesla
  async function handleSetPassword(userId: string) {
    const newPwd = window.prompt("Zadej nové heslo (min. 6 znaků):");
    if (!newPwd) return;
    if (newPwd.length < 6) {
      alert("Heslo musí mít alespoň 6 znaků.");
      return;
    }
    try {
      setBusy(true);
      await api.patch(`/users/${userId}/password`, { newPassword: newPwd });
      alert("Heslo nastaveno.");
    } catch (e: any) {
      alert(e?.response?.status === 403 ? "Nemáš oprávnění." : "Nepodařilo se nastavit heslo.");
      console.error(e);
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="max-h-[85vh] w-[min(1100px,96vw)] grid grid-rows-[auto,1fr,auto] overflow-hidden rounded-2xl bg-white shadow-xl">
        {/* header */}
        <div className="flex items-center justify-between border-b p-4">
          <h2 className="text-lg font-semibold">Přístupy k motorům</h2>
          <div className="flex items-center gap-2">
            <button
              className="rounded-lg border px-3 py-1 text-sm hover:bg-gray-50 disabled:opacity-50"
              onClick={loadUsersAndAccess}
              disabled={busy}
              title="Obnovit seznam"
            >
              ⟳ Obnovit
            </button>
            <button className="rounded-lg border px-3 py-1 text-sm hover:bg-gray-50" onClick={onClose}>
              Zavřít
            </button>
          </div>
        </div>

        {/* scrollovací obsah */}
        <div className="min-h-0 overflow-y-auto p-3 space-y-3">
          {/* Vytvoření uživatele */}
          <div className="flex flex-wrap items-end gap-3 rounded-xl border p-3">
            <div className="w-64">
              <label className="mb-1 block text-xs text-gray-600">Email</label>
              <input
                className="w-full rounded-lg border px-2 py-1"
                value={creating.email}
                onChange={(e) => setCreating((s) => ({ ...s, email: e.target.value }))}
                placeholder="uzivatel@example.com"
              />
            </div>

            <div className="w-56">
              <label className="mb-1 block text-xs text-gray-600">Jméno (volitelné)</label>
              <input
                className="w-full rounded-lg border px-2 py-1"
                value={creating.name}
                onChange={(e) => setCreating((s) => ({ ...s, name: e.target.value }))}
                placeholder="Jméno Příjmení"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs text-gray-600">Role</label>
              <select
                className="rounded-lg border px-2 py-1"
                value={creating.role}
                onChange={(e) =>
                  setCreating((s) => ({ ...s, role: (e.target.value as "ADMIN" | "PLANNER" | "VIEWER") }))
                }
              >
                <option value="VIEWER">VIEWER</option>
                <option value="PLANNER">PLANNER</option>
                <option value="ADMIN">ADMIN</option>
              </select>
            </div>

            <div className="w-56">
              <label className="mb-1 block text-xs text-gray-600">Heslo</label>
              <input
                className="w-full rounded-lg border px-2 py-1"
                value={creating.tempPassword}
                onChange={(e) => setCreating((s) => ({ ...s, tempPassword: e.target.value }))}
                placeholder="(zvol heslo)"
              />
            </div>

            <button
              className="ml-auto rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
              onClick={createUser}
              disabled={busy || !EMAIL_RX.test(creating.email.trim())}
            >
              {busy ? "Vytvářím…" : "+ Přidat uživatele"}
            </button>
          </div>

          {/* Tabulka přístupů */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 bg-white p-2 text-left">Uživatel</th>
                  <th className="p-2 text-left">Akce</th>
                  {motors.map((m) => (
                    <th key={m.id} className="p-2 text-left">
                      {m.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t">
                    <td className="sticky left-0 z-10 bg-white p-2">
                      <div className="font-medium">{u.name || u.email}</div>
                      <div className="text-xs text-gray-500">{u.email}</div>
                    </td>

                    {/* Akce pro hesla – jen ruční nastavení */}
                    <td className="p-2">
                      <div className="flex flex-wrap gap-2">
                        <button
                          className="rounded border px-2 py-1 text-xs hover:bg-gray-50 disabled:opacity-50"
                          onClick={() => handleSetPassword(u.id)}
                          disabled={busy}
                          title="Ruční nastavení hesla"
                        >
                          Nastavit heslo…
                        </button>
                      </div>
                    </td>

                    {motors.map((m) => {
                      const k = `${u.id}|${m.id}`;
                      const row = map[k];
                      const hasRead = !!row;
                      const canWrite = !!row?.canWrite;
                      return (
                        <td key={m.id} className="p-2">
                          <div className="flex items-center gap-3">
                            <label className="inline-flex items-center gap-1">
                              <input
                                type="checkbox"
                                checked={hasRead}
                                onChange={() => toggleRead(u.id, m.id)}
                              />
                              <span>Read</span>
                            </label>
                            <label className="inline-flex items-center gap-1">
                              <input
                                type="checkbox"
                                checked={canWrite}
                                disabled={!hasRead}
                                onChange={() => toggleWrite(u.id, m.id)}
                              />
                              <span>Write</span>
                            </label>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* footer */}
        <div className="border-t p-3 text-xs text-gray-600">
          Write implikuje Read. Zrušení Read smaže záznam přístupu.
        </div>
      </div>
    </div>
  );
}
